/*
 * Rice Time — "Catch the Grain" hero minigame
 * ---------------------------------------------------------------
 * Owns ALL grain interaction. The ambient sizzle-pop easter egg lives here
 * too (moved out of rt-motion.js), so there is exactly one click handler
 * for grains and no chance of the two fighting.
 *
 * States: idle -> playing -> over
 *   idle    ambient CSS drift (rt-motion.css). First grain click starts play.
 *   playing game-owned grains driven by the Web Animations API.
 *   over    game-over panel; hero restored on exit.
 *
 * Why WAAPI: a CSS animation cannot change speed mid-flight without
 * restarting and visibly jumping. `anim.playbackRate *= 1.01` scales an
 * in-flight grain smoothly, stays compositor-driven, and `finish` gives
 * exact escape detection with no polling.
 *
 * Reduced motion: the grain layer is display:none, so nothing is bound and
 * no game exists. That is intentional and matches the pre-game behaviour.
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var CFG = {
    POINTS_GOOD: 5,
    POINTS_GOLD: 50,
    COMBO_STEP: 5, // hits per multiplier tier
    COMBO_MAX: 8,
    SPEED_STEP: 1.01, // +1% per successful hit
    START_LIVES: 3,
    LIFE_FIRST: 1000, // then every LIFE_EVERY
    LIFE_EVERY: 5000,
    LIVES_SHOWN: 5,
    BURN_MAX: 10,
    BURN_DRAIN_PER_HIT: 0.25, // 4 clean hits undo one escape
    BAD_UNLOCK: 100,
    BAD_CHANCE_MAX: 0.2,
    GOLD_CHANCE: 0.04,
    CHILI_CHANCE: 0.025,
    CHILI_RATE: 0.4,
    CHILI_MS: 4000,
    // ms to cross the hero at playbackRate 1. Higher = slower start.
    // Was 9000; doubled to halve the opening speed, which was too hard to
    // read before the +1% ramp had any room to build.
    BASE_DUR: 18000,
    SPAWN_BASE: 6,
    SPAWN_PER: 500, // +1 concurrent grain per 500 points
    SPAWN_MAX: 14,
    IDLE_EXIT_MS: 25000,
    MAX_POPS: 12,
    POP_LIFETIME: 1400,
    RESPAWN_DELAY: 1200,
    BEST_KEY: 'rt-grain-best',
  };

  /* ---------------------------------------------------------------
   * Small helpers
   * --------------------------------------------------------------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function readBest() {
    try {
      return parseInt(window.localStorage.getItem(CFG.BEST_KEY), 10) || 0;
    } catch (e) {
      return 0; // private mode throws on access
    }
  }

  function writeBest(v) {
    try {
      window.localStorage.setItem(CFG.BEST_KEY, String(v));
    } catch (e) {
      /* nothing we can do, and nothing that should break the game */
    }
  }

  function readConfig(hero) {
    var node = hero.querySelector('[data-rt-game-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ---------------------------------------------------------------
   * The sizzle pop — shared by the ambient easter egg and the game
   * --------------------------------------------------------------- */

  function pop(container, x, y) {
    if (container.querySelectorAll('.rt-grain-pop').length >= CFG.MAX_POPS) return;

    var node = el('span', 'rt-grain-pop');
    node.style.left = x + 'px';
    node.style.top = y + 'px';
    node.appendChild(el('span', 'rt-grain-pop__flash'));

    for (var i = 0; i < 3; i++) {
      var wisp = el('span', 'rt-grain-pop__wisp');
      wisp.style.setProperty('--rt-steam-delay', i * 90 + 'ms');
      wisp.style.setProperty('--rt-steam-dur', 760 + i * 120 + 'ms');
      // Spread with `left`, not transform — rtSteam animates transform.
      wisp.style.left = -2 + (i - 1) * 7 + 'px';
      node.appendChild(wisp);
    }

    container.appendChild(node);
    setTimeout(function () {
      if (node.parentNode) node.parentNode.removeChild(node);
    }, CFG.POP_LIFETIME);
  }

  function popAt(container, grain) {
    var box = grain.getBoundingClientRect();
    var host = container.getBoundingClientRect();
    pop(container, box.left - host.left + box.width / 2, box.top - host.top + box.height / 2);
  }

  /* ---------------------------------------------------------------
   * Game
   * --------------------------------------------------------------- */

  function Game(hero, container, config) {
    this.hero = hero;
    this.container = container;
    this.config = config;
    this.state = 'idle';

    this.timers = [];
    this.grains = []; // { el, anim, type }
    this.ambient = Array.prototype.slice.call(container.querySelectorAll('.rt-grain'));

    this.bound = {};
    this.ui = null;
    this.best = readBest();

    this.reset(true);
  }

  Game.prototype.reset = function (initial) {
    this.score = 0;
    this.lives = CFG.START_LIVES;
    this.streak = 0;
    this.burn = 0;
    this.speed = 1;
    this.chiliUntil = 0;
    this.awardedLives = {};
    this.rewardTier = -1;
    if (!initial) this.render();
  };

  /* ---- lifecycle ------------------------------------------------ */

  Game.prototype.start = function () {
    if (this.state === 'playing') return;
    this.state = 'playing';
    this.reset(true);

    this.hero.classList.add('rt-hero--gaming');

    // Park the ambient grains; the game owns the field now.
    this.ambient.forEach(function (g) {
      g.style.display = 'none';
    });

    this.buildUI();
    this.render();
    this.tickSpawn();
    this.bumpIdle();

    this.loop = setInterval(this.tickSpawn.bind(this), 650);
    this.timers.push(this.loop);
  };

  Game.prototype.restart = function () {
    this.clearGrains();
    this.reset();
    this.state = 'playing';
    if (this.ui) {
      this.ui.panel.hidden = true;
      this.ui.hud.hidden = false;
    }
    this.render();
    this.bumpIdle();
  };

  Game.prototype.exit = function () {
    this.clearGrains();
    clearInterval(this.loop);
    this.timers.forEach(clearTimeout);
    this.timers = [];

    this.hero.classList.remove('rt-hero--gaming');
    this.ambient.forEach(function (g) {
      g.style.display = '';
    });

    if (this.ui && this.ui.root.parentNode) this.ui.root.parentNode.removeChild(this.ui.root);
    this.ui = null;
    this.state = 'idle';
  };

  Game.prototype.gameOver = function () {
    this.state = 'over';
    this.clearGrains();
    clearInterval(this.loop);

    if (this.score > this.best) {
      this.best = this.score;
      writeBest(this.best);
    }
    this.showPanel();
  };

  /* ---- spawning -------------------------------------------------- */

  Game.prototype.targetCount = function () {
    return Math.min(CFG.SPAWN_MAX, CFG.SPAWN_BASE + Math.floor(this.score / CFG.SPAWN_PER));
  };

  Game.prototype.tickSpawn = function () {
    if (this.state !== 'playing') return;
    while (this.grains.length < this.targetCount()) this.spawn();
  };

  Game.prototype.pickType = function () {
    var r = Math.random();
    if (r < CFG.CHILI_CHANCE) return 'chili';
    if (r < CFG.CHILI_CHANCE + CFG.GOLD_CHANCE) return 'gold';
    if (this.score >= CFG.BAD_UNLOCK) {
      var chance = Math.min(CFG.BAD_CHANCE_MAX, 0.05 + this.score / 60000);
      if (Math.random() < chance) return 'bad';
    }
    return 'good';
  };

  Game.prototype.spawn = function () {
    var type = this.pickType();
    var node = el('span', 'rt-grain rt-grain--game rt-grain--' + type);
    node.dataset.type = type;
    node.style.left = 4 + Math.random() * 92 + '%';

    if (type === 'chili') {
      node.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><use href="#rt-chili"/></svg>';
    }

    this.container.appendChild(node);

    var travel = this.container.getBoundingClientRect().height + 140;
    var drift = (Math.random() * 90 - 45) | 0;
    var spin = 180 + Math.random() * 260;

    var anim = node.animate(
      [
        { transform: 'translate3d(0,0,0) rotate(0deg)', opacity: 0 },
        { opacity: 0.85, offset: 0.08 },
        { opacity: 0.85, offset: 0.9 },
        {
          transform: 'translate3d(' + drift + 'px,' + -travel + 'px,0) rotate(' + spin + 'deg)',
          opacity: 0,
        },
      ],
      { duration: CFG.BASE_DUR, easing: 'linear', fill: 'forwards' }
    );

    anim.playbackRate = this.speed * (type === 'gold' ? 1.45 : 1);

    var self = this;
    var entry = { el: node, anim: anim, type: type };
    anim.onfinish = function () {
      self.escaped(entry);
    };

    this.grains.push(entry);
  };

  Game.prototype.remove = function (entry) {
    var i = this.grains.indexOf(entry);
    // Guard the -1 case explicitly: splice(-1, 1) removes the LAST grain,
    // silently corrupting the field when an entry is removed twice.
    if (i !== -1) this.grains.splice(i, 1);
    try {
      entry.anim.cancel();
    } catch (e) {}
    if (entry.el.parentNode) entry.el.parentNode.removeChild(entry.el);
  };

  Game.prototype.clearGrains = function () {
    this.grains.slice().forEach(this.remove.bind(this));
    this.grains = [];
  };

  /* ---- rules ----------------------------------------------------- */

  Game.prototype.multiplier = function () {
    return Math.min(CFG.COMBO_MAX, 1 + Math.floor(this.streak / CFG.COMBO_STEP));
  };

  Game.prototype.applySpeed = function () {
    var rate = this.speed * (Date.now() < this.chiliUntil ? CFG.CHILI_RATE : 1);
    this.grains.forEach(function (g) {
      g.anim.playbackRate = rate * (g.type === 'gold' ? 1.45 : 1);
    });
  };

  Game.prototype.hit = function (entry) {
    if (entry.el.dataset.dead) return;
    entry.el.dataset.dead = '1';
    this.bumpIdle();
    popAt(this.container, entry.el);

    if (entry.type === 'bad') {
      this.streak = 0;
      this.loseLife('bad');
      this.remove(entry);
      this.render();
      return;
    }

    if (entry.type === 'chili') {
      this.chiliUntil = Date.now() + CFG.CHILI_MS;
      this.hero.classList.add('rt-hero--chilled');
      var self = this;
      this.timers.push(
        setTimeout(function () {
          self.hero.classList.remove('rt-hero--chilled');
          self.applySpeed();
        }, CFG.CHILI_MS)
      );
      this.toast('Cooled down');
      this.remove(entry);
      this.applySpeed();
      this.render();
      return;
    }

    var base = entry.type === 'gold' ? CFG.POINTS_GOLD : CFG.POINTS_GOOD;
    this.streak += 1;
    this.score += base * this.multiplier();
    this.speed *= CFG.SPEED_STEP;
    this.burn = Math.max(0, this.burn - CFG.BURN_DRAIN_PER_HIT);

    if (entry.type === 'gold') this.toast('+' + base * this.multiplier());

    this.remove(entry);
    this.applySpeed();
    this.checkLives();
    this.checkReward();
    this.render();
  };

  Game.prototype.escaped = function (entry) {
    if (this.state !== 'playing') return;
    this.remove(entry);
    // Only good/gold grains matter — letting a hazard drift away is fine.
    if (entry.type === 'bad' || entry.type === 'chili') return;

    this.streak = 0;
    this.burn += 1;
    if (this.burn >= CFG.BURN_MAX) {
      this.burn = 0;
      this.loseLife('burn');
    }
    this.render();
  };

  Game.prototype.loseLife = function (cause) {
    this.lives -= 1;
    this.hero.classList.add('rt-hero--hurt');
    var self = this;
    this.timers.push(
      setTimeout(function () {
        self.hero.classList.remove('rt-hero--hurt');
      }, 420)
    );
    this.toast(cause === 'burn' ? 'Burnt!' : 'Bad grain!');
    if (this.lives <= 0) this.gameOver();
  };

  Game.prototype.checkLives = function () {
    var thresholds = [CFG.LIFE_FIRST];
    for (var t = CFG.LIFE_EVERY; t <= this.score; t += CFG.LIFE_EVERY) thresholds.push(t);

    for (var i = 0; i < thresholds.length; i++) {
      var v = thresholds[i];
      if (this.score >= v && !this.awardedLives[v]) {
        this.awardedLives[v] = true; // once per run, never re-fires
        this.lives += 1;
        this.toast('Extra life!');
      }
    }
  };

  /* ---- rewards --------------------------------------------------- */

  Game.prototype.tiers = function () {
    return (this.config && this.config.tiers) || [];
  };

  Game.prototype.checkReward = function () {
    var tiers = this.tiers();
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (this.score >= tiers[i].score) {
        if (i > this.rewardTier) {
          this.rewardTier = i;
          this.toast(tiers[i].percent + '% off unlocked');
        }
        return;
      }
    }
  };

  /*
   * The single seam for reward delivery.
   *
   * Mints a unique, single-use, 24-hour code from the rewards Worker. There
   * are deliberately NO static fallback codes — a static code that leaks is
   * a permanent public discount, which is the whole reason this exists.
   *
   * Rate-limited is NOT an error: a player who already claimed today is
   * shown the code they earned, replayed from localStorage.
   *
   * Any genuine failure rejects, and the panel renders the contact form so
   * a deserving player is followed up manually.
   */
  Game.prototype.claimReward = function (tier) {
    var t = this.tiers()[tier];
    var endpoint = this.config && this.config.endpoint;
    if (!t) return Promise.reject(new Error('no_tier'));
    if (!endpoint) return Promise.reject(new Error('not_configured'));

    var cached = this.readClaim();
    if (cached && cached.tier === tier) return Promise.resolve(cached);

    var self = this;
    var score = this.score;

    // Without a timeout a hung request leaves the player staring at nothing.
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, 8000);

    return fetch(endpoint.replace(/\/$/, '') + '/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier: tier, score: score }),
      signal: controller.signal,
    })
      .then(function (res) {
        clearTimeout(timer);
        if (res.status === 429) {
          var prior = self.readClaim();
          if (prior) return prior;
          throw new Error('rate_limited');
        }
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .then(function (reward) {
        if (!reward || !reward.code) throw new Error('empty');
        reward.tier = tier;
        self.writeClaim(reward);
        return reward;
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  };

  /* One code per browser per day — a replay shows the same code rather
     than burning another mint. */
  Game.prototype.claimKey = function () {
    return 'rt-grain-claimed-' + new Date().toISOString().slice(0, 10);
  };

  Game.prototype.readClaim = function () {
    try {
      var raw = window.localStorage.getItem(this.claimKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  Game.prototype.writeClaim = function (reward) {
    try {
      window.localStorage.setItem(this.claimKey(), JSON.stringify(reward));
    } catch (e) {
      /* private mode — the player just may get a second code tomorrow */
    }
  };

  /* ---- UI -------------------------------------------------------- */

  Game.prototype.buildUI = function () {
    var root = el('div', 'rt-game');
    root.setAttribute('aria-hidden', 'true');

    var hud = el('div', 'rt-game__hud');

    var score = el('div', 'rt-game__stat');
    score.appendChild(el('span', 'rt-game__label', 'Score'));
    var scoreVal = el('span', 'rt-game__value', '0');
    score.appendChild(scoreVal);

    var combo = el('div', 'rt-game__stat rt-game__stat--combo');
    var comboVal = el('span', 'rt-game__combo', '');
    combo.appendChild(comboVal);

    var lives = el('div', 'rt-game__stat');
    lives.appendChild(el('span', 'rt-game__label', 'Lives'));
    var livesVal = el('span', 'rt-game__lives');
    lives.appendChild(livesVal);

    var burn = el('div', 'rt-game__stat');
    burn.appendChild(el('span', 'rt-game__label', 'Burnt'));
    var burnBar = el('span', 'rt-game__burn');
    var burnFill = el('i', 'rt-game__burn-fill');
    burnBar.appendChild(burnFill);
    burn.appendChild(burnBar);

    var controls = el('div', 'rt-game__controls');
    var restart = el('button', 'rt-game__btn', 'Restart');
    var exit = el('button', 'rt-game__btn rt-game__btn--icon', '×');
    // Focusable children inside an aria-hidden subtree would be a trap for
    // keyboard users, so take them out of the tab order explicitly.
    [restart, exit].forEach(function (b) {
      b.type = 'button';
      b.tabIndex = -1;
    });
    exit.setAttribute('aria-label', 'Exit game');
    controls.appendChild(restart);
    controls.appendChild(exit);

    hud.appendChild(score);
    hud.appendChild(combo);
    hud.appendChild(lives);
    hud.appendChild(burn);
    hud.appendChild(controls);

    var toast = el('div', 'rt-game__toast');
    toast.hidden = true;

    var paused = el('div', 'rt-game__paused', 'Paused');
    paused.hidden = true;

    var panel = el('div', 'rt-game__panel');
    panel.hidden = true;

    root.appendChild(hud);
    root.appendChild(toast);
    root.appendChild(paused);
    root.appendChild(panel);
    this.hero.appendChild(root);

    var self = this;
    restart.addEventListener('click', function () {
      self.restart();
    });
    exit.addEventListener('click', function () {
      self.exit();
    });

    this.ui = {
      root: root,
      hud: hud,
      score: scoreVal,
      combo: comboVal,
      lives: livesVal,
      burn: burnFill,
      toast: toast,
      paused: paused,
      panel: panel,
    };
  };

  Game.prototype.render = function () {
    if (!this.ui) return;
    this.ui.score.textContent = this.score.toLocaleString();

    var m = this.multiplier();
    this.ui.combo.textContent = m > 1 ? '×' + m : '';
    this.ui.combo.classList.toggle('is-on', m > 1);

    var shown = Math.min(this.lives, CFG.LIVES_SHOWN);
    var html = '';
    for (var i = 0; i < shown; i++) html += '<i></i>';
    if (this.lives > CFG.LIVES_SHOWN) html += '<b>+' + (this.lives - CFG.LIVES_SHOWN) + '</b>';
    this.ui.lives.innerHTML = html;

    this.ui.burn.style.width = Math.min(100, (this.burn / CFG.BURN_MAX) * 100) + '%';
  };

  Game.prototype.toast = function (msg) {
    if (!this.ui) return;
    var t = this.ui.toast;
    t.textContent = msg;
    t.hidden = false;
    t.classList.remove('is-in');
    void t.offsetWidth;
    t.classList.add('is-in');
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () {
      t.hidden = true;
    }, 1100);
    this.timers.push(this._toastT);
  };

  Game.prototype.showPanel = function () {
    if (!this.ui) return;
    var p = this.ui.panel;
    var self = this;
    this.ui.hud.hidden = true;
    p.innerHTML = '';

    p.appendChild(el('p', 'rt-game__panel-kicker', 'Game over'));
    p.appendChild(el('p', 'rt-game__panel-score', this.score.toLocaleString()));
    p.appendChild(el('p', 'rt-game__panel-best', 'Best ' + this.best.toLocaleString()));

    var actions = el('div', 'rt-game__panel-actions');
    var again = el('button', 'rt-btn rt-btn--on-dark', 'Play again');
    again.type = 'button';
    again.tabIndex = -1;
    again.addEventListener('click', function () {
      self.restart();
    });

    var shop = el('a', 'rt-btn rt-btn--ghost rt-game__shop', 'Shop the merch');
    shop.href = (this.config && this.config.shopUrl) || '/collections/all';
    shop.tabIndex = -1;

    actions.appendChild(again);
    actions.appendChild(shop);

    /* Attach actions BEFORE the reward block: the reward is inserted
       relative to it, and insertBefore throws NotFoundError if the
       reference node is not yet a child. */
    p.appendChild(actions);

    if (this.config && this.config.rewards && this.rewardTier >= 0) {
      var pending = el('div', 'rt-game__reward');
      pending.appendChild(el('p', 'rt-game__reward-line', 'Cooking up your code…'));
      p.insertBefore(pending, actions);

      var tierIndex = this.rewardTier;
      var tierPct = (this.tiers()[tierIndex] || {}).percent;

      this.claimReward(tierIndex).then(
        function (reward) {
          pending.innerHTML = '';
          pending.appendChild(
            el('p', 'rt-game__reward-line', 'You unlocked ' + reward.percent + '% off')
          );

          var btn = el('button', 'rt-game__code');
          btn.type = 'button';
          btn.tabIndex = -1;
          btn.appendChild(el('span', null, reward.code));
          var hint = el('em', null, 'copy');
          btn.appendChild(hint);
          btn.addEventListener('click', function () {
            try {
              navigator.clipboard.writeText(reward.code);
              hint.textContent = 'copied';
            } catch (e) {
              hint.textContent = 'select it';
            }
          });
          pending.appendChild(btn);

          if (reward.expiresAt) {
            pending.appendChild(
              el('p', 'rt-game__reward-expiry', 'Single use · expires in 24 hours')
            );
          }
        },
        function (err) {
          self.renderRescue(pending, tierIndex, tierPct, err && err.message);
        }
      );
    }

    p.hidden = false;
  };

  /*
   * Honest failure path.
   *
   * Deliberately does NOT depend on the rewards Worker — if that is down it
   * cannot email anyone either. Shopify's own contact form posts to the
   * store and mails the contact address, so this works even when the
   * backend is completely unreachable.
   */
  Game.prototype.renderRescue = function (box, tier, percent, reason) {
    var self = this;
    box.innerHTML = '';
    box.className = 'rt-game__reward rt-game__reward--rescue';

    box.appendChild(
      el('p', 'rt-game__reward-line', 'You earned ' + percent + '% off — but we could not issue the code.')
    );
    box.appendChild(
      el('p', 'rt-game__rescue-note', 'Leave your email and we will send it over by hand.')
    );

    var form = el('form', 'rt-game__rescue');
    form.method = 'post';
    form.action = '/contact#contact_form';
    form.acceptCharset = 'UTF-8';

    var hiddenType = document.createElement('input');
    hiddenType.type = 'hidden';
    hiddenType.name = 'form_type';
    hiddenType.value = 'contact';

    var hiddenUtf = document.createElement('input');
    hiddenUtf.type = 'hidden';
    hiddenUtf.name = 'utf8';
    hiddenUtf.value = '✓';

    var email = document.createElement('input');
    email.type = 'email';
    email.name = 'contact[email]';
    email.required = true;
    email.placeholder = 'you@example.com';
    email.className = 'rt-game__rescue-input';
    email.tabIndex = -1;

    var body = document.createElement('input');
    body.type = 'hidden';
    body.name = 'contact[body]';
    body.value =
      'Rice Time minigame reward could not be issued.\n' +
      'Score: ' + this.score + '\n' +
      'Tier: ' + (tier + 1) + ' (' + percent + '% off)\n' +
      'Reason: ' + (reason || 'unknown') + '\n' +
      'Page: ' + window.location.href;

    var send = el('button', 'rt-game__rescue-send', 'Send');
    send.type = 'submit';
    send.tabIndex = -1;

    form.appendChild(hiddenType);
    form.appendChild(hiddenUtf);
    form.appendChild(email);
    form.appendChild(body);
    form.appendChild(send);
    box.appendChild(form);

    form.addEventListener('submit', function (event) {
      if (!email.value) return;
      event.preventDefault();
      // URLSearchParams, not FormData: a native Shopify contact form posts
      // application/x-www-form-urlencoded, so mimic that exactly rather
      // than sending multipart and hoping their handler accepts it.
      var data = new URLSearchParams(new FormData(form));
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('contact_failed');
          box.innerHTML = '';
          box.appendChild(
            el('p', 'rt-game__reward-line', 'Got it — your code is on its way to ' + email.value + '.')
          );
        })
        .catch(function () {
          // Last resort: a real form post always works, even if it navigates.
          form.submit();
        });
    });
  };

  /* ---- idle / pause ---------------------------------------------- */

  Game.prototype.bumpIdle = function () {
    var self = this;
    clearTimeout(this._idleT);
    this._idleT = setTimeout(function () {
      if (self.state === 'playing') self.exit();
    }, CFG.IDLE_EXIT_MS);
    this.timers.push(this._idleT);
  };

  Game.prototype.setPaused = function (on) {
    if (this.state !== 'playing' || !this.ui) return;
    this.ui.paused.hidden = !on;
    this.grains.forEach(function (g) {
      try {
        on ? g.anim.pause() : g.anim.play();
      } catch (e) {}
    });
    if (on) clearInterval(this.loop);
    else {
      this.loop = setInterval(this.tickSpawn.bind(this), 650);
      this.timers.push(this.loop);
    }
  };

  /* ---------------------------------------------------------------
   * Wiring
   * --------------------------------------------------------------- */

  function initHero(hero) {
    if (hero.dataset.rtGameBound === 'true') return;
    var container = hero.querySelector('.rt-hero__grains--live');
    if (!container) return;
    hero.dataset.rtGameBound = 'true';

    var config = readConfig(hero) || {};
    var game = new Game(hero, container, config);
    window.rtGrainGame = game; // read-only surface for debugging and tests

    container.addEventListener('click', function (event) {
      var node = event.target.closest('.rt-grain');
      if (!node) return;

      if (game.state === 'playing') {
        var entry = null;
        for (var i = 0; i < game.grains.length; i++) {
          if (game.grains[i].el === node) {
            entry = game.grains[i];
            break;
          }
        }
        if (entry) game.hit(entry);
        return;
      }

      if (game.state === 'over') return;

      // idle: the ambient easter egg, and the trigger to start a run.
      if (node.style.visibility === 'hidden') return;
      popAt(container, node);
      node.style.visibility = 'hidden';
      setTimeout(function () {
        if (!document.contains(node)) return;
        node.style.left = Math.round(Math.random() * 100) + '%';
        node.style.animation = 'none';
        void node.offsetWidth;
        node.style.animation = '';
        node.style.visibility = '';
      }, CFG.RESPAWN_DELAY);

      if (config.enabled) {
        game.start();
        game.score = CFG.POINTS_GOOD;
        game.streak = 1;
        game.render();
      }
    });

    // Hovering a game grain pauses it so it can be caught. Colour comes
    // from CSS; only the pause needs script.
    container.addEventListener('mouseover', function (event) {
      if (game.state !== 'playing') return;
      var node = event.target.closest('.rt-grain--game');
      if (!node) return;
      game.grains.forEach(function (g) {
        if (g.el === node) {
          try {
            g.anim.pause();
          } catch (e) {}
        }
      });
    });

    container.addEventListener('mouseout', function (event) {
      if (game.state !== 'playing') return;
      var node = event.target.closest('.rt-grain--game');
      if (!node) return;
      game.grains.forEach(function (g) {
        if (g.el === node) {
          try {
            g.anim.play();
          } catch (e) {}
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && game.state !== 'idle') game.exit();
    });

    document.addEventListener('visibilitychange', function () {
      game.setPaused(document.hidden);
    });
  }

  function init(root) {
    if (reduceMotion.matches) return;
    var heroes = (root || document).querySelectorAll('.rt-hero');
    Array.prototype.forEach.call(heroes, initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
